import React from 'react'
import CSS from 'csstype'
import {
  Rect,
  HorizontalSide,
  VerticalSide,
  Orientation,
  Side,
} from 'modules/shares/types'
import { ReactComponent as TopLeft } from '@sketch/icons/inspector/top_left-svg'
import { ReactComponent as TopRight } from '@sketch/icons/inspector/top_right-svg'
import { ReactComponent as BottomLeft } from '@sketch/icons/inspector/bottom_left-svg'
import { ReactComponent as BottomRight } from '@sketch/icons/inspector/bottom_right-svg'

// The corner SVGs are drawn using an outer border which means they are drawn at a 1
// pixel offset.
const cornerOffset = 1

const IndicatorLine: React.FC<{
  side: Side
}> = ({ side }) => {
  const style: CSS.Properties = {
    position: 'absolute',
    borderRadius: '0.5px',
  }
  const cornerMargin = '7px'

  const border = '1px solid #ff0000'
  switch (side) {
    case 'top':
      style['borderTop'] = border
      style['top'] = '0px'
      style['left'] = cornerMargin
      style['right'] = cornerMargin
      break

    case 'right':
      style['borderRight'] = border
      style['right'] = '0px'
      style['top'] = cornerMargin
      style['bottom'] = cornerMargin
      break

    case 'bottom':
      style['borderBottom'] = border
      style['bottom'] = '0px'
      style['left'] = cornerMargin
      style['right'] = cornerMargin
      break

    case 'left':
      style['borderLeft'] = border
      style['left'] = '0px'
      style['top'] = cornerMargin
      style['bottom'] = cornerMargin
      break
  }

  return <div style={style} />
}

const DashedLine: React.FC<{
  location: [Orientation, VerticalSide, HorizontalSide]
  frame: Rect
}> = ({ location, frame }) => {
  const style: CSS.Properties = {
    position: 'absolute',
  }
  const border = '1px dashed #ff0000'
  const indicatorMargin = 3

  switch (location.join(', ')) {
    case 'horizontal, top, left':
      style['borderTop'] = border
      style['top'] = `${frame.y}px`
      style['left'] = 0
      style['width'] = `${Math.max(0, frame.x - indicatorMargin)}px`

      break

    case 'horizontal, top, right':
      style['borderTop'] = border
      style['top'] = `${frame.y}px`
      style['left'] = `${frame.x + frame.width + indicatorMargin}px`
      style['right'] = 0
      break

    case 'horizontal, bottom, left':
      style['borderTop'] = border
      style['top'] = `${frame.y + frame.height - cornerOffset}px`
      style['left'] = 0
      style['width'] = `${Math.max(0, frame.x - indicatorMargin)}px`
      break

    case 'horizontal, bottom, right':
      style['borderBottom'] = border
      style['top'] = `${frame.y + frame.height - cornerOffset}px`
      style['left'] = `${frame.x + frame.width + indicatorMargin}px`
      style['right'] = 0
      break

    case 'vertical, top, left':
      style['borderLeft'] = border
      style['top'] = 0
      style['left'] = `${frame.x}px`
      style['height'] = `${Math.max(0, frame.y - indicatorMargin)}px`
      break

    case 'vertical, top, right':
      style['borderLeft'] = border
      style['top'] = 0
      style['left'] = `${frame.x + frame.width - cornerOffset}px`
      style['height'] = `${Math.max(0, frame.y - indicatorMargin)}px`
      break

    case 'vertical, bottom, left':
      style['borderLeft'] = border
      style['top'] = `${frame.y + frame.height + indicatorMargin}px`
      style['bottom'] = 0
      style['left'] = `${frame.x}px`
      break

    case 'vertical, bottom, right':
      style['borderLeft'] = border
      style['top'] = `${frame.y + frame.height + indicatorMargin}px`
      style['bottom'] = 0
      style['left'] = `${frame.x + frame.width - cornerOffset}px`
      break
  }

  return <div style={style} />
}

const LargeIndicator: React.FC<{ frame: Rect }> = ({ frame }) => {
  const style: CSS.Properties = {
    position: 'absolute',
    top: `${frame.y}px`,
    left: `${frame.x}px`,
    width: `${frame.width}px`,
    height: `${frame.height}px`,
  }

  const cornerStyles: CSS.Properties = {
    position: 'absolute',
    width: '6px',
    height: '6px',
  }

  return (
    <div style={style}>
      <TopLeft
        style={{ top: -cornerOffset, left: -cornerOffset, ...cornerStyles }}
      />
      <TopRight
        style={{ top: -cornerOffset, right: -cornerOffset, ...cornerStyles }}
      />
      <BottomLeft
        style={{ bottom: -cornerOffset, left: -cornerOffset, ...cornerStyles }}
      />
      <BottomRight
        style={{ bottom: -cornerOffset, right: -cornerOffset, ...cornerStyles }}
      />

      <IndicatorLine side="top" />
      <IndicatorLine side="right" />
      <IndicatorLine side="bottom" />
      <IndicatorLine side="left" />
    </div>
  )
}

const SmallIndicator: React.FC<{ frame: Rect }> = ({ frame }) => {
  return (
    <div
      style={{
        position: 'absolute',
        left: `${frame.x}px`,
        top: `${frame.y}px`,
        width: `${frame.width}px`,
        height: `${frame.height}px`,
        border: '1px solid #ff0000',
      }}
    />
  )
}

const Selection: React.FC<{ frame: Rect }> = ({ frame, ...rest }) => {
  const isSmallSelection = frame.width <= 12 || frame.height <= 12

  return (
    <div {...rest}>
      {/* Solid red lines for the current selection */}
      {isSmallSelection ? (
        <SmallIndicator frame={frame} />
      ) : (
        <LargeIndicator frame={frame} />
      )}

      {/* Dashed red lines that go beyond the limits of the selection */}
      <DashedLine location={['horizontal', 'top', 'left']} frame={frame} />
      <DashedLine location={['horizontal', 'top', 'right']} frame={frame} />

      <DashedLine location={['horizontal', 'bottom', 'left']} frame={frame} />
      <DashedLine location={['horizontal', 'bottom', 'right']} frame={frame} />

      <DashedLine location={['vertical', 'top', 'left']} frame={frame} />
      <DashedLine location={['vertical', 'top', 'right']} frame={frame} />

      <DashedLine location={['vertical', 'bottom', 'left']} frame={frame} />
      <DashedLine location={['vertical', 'bottom', 'right']} frame={frame} />
    </div>
  )
}

export default Selection
